import Container from 'components/container/Container';
import style from './VideoPlayer.module.scss';
import { Body, Small } from 'components/text';
import PlayButton from '../../icons/PlayButton';
import Sound from '../../icons/Sound';
import Button from 'components/Button/BaseButton';
import { useCallback, useEffect, useRef, useState } from 'react';
import Pause from '../../icons/Pause';
import Mute from '../../icons/Mute';
import ProgressBar from './ProgressBar';
import useDoubleTap from 'hooks/useDoubleTap';
import { formatDuration } from './utils';
import FullscreenToggle from 'blocks/MylerSection/icons/FullscreenToggle';

type VideoPlayerProps = {
  src: string;
  muted?: boolean;
  autoPlay: boolean;
  thumbnail?: string;
  formats?: string[];
  controlsTs?: number;
  isMobile?: boolean;
} & React.HTMLProps<HTMLVideoElement>;

const VideoPlayer = ({
  src,
  thumbnail,
  muted = false,
  autoPlay = false,
  formats = ['video/mp4'],
  controlsTs = 3000,
  isMobile = false,
  ...rest
}: VideoPlayerProps) => {
  const videoRef = useRef<any>(null);
  const timeoutRef = useRef<any>(0);

  const [playTime, setPlayTime] = useState(0);
  const [mute, setMute] = useState(muted);
  const [percentage, setPercentage] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [showTimer, setShowTimer] = useState(false);

  const getDuration = (): string => {
    const video = videoRef.current;
    if (!video) return '0';
    return formatDuration(video.duration);
  };

  const handlePlay = useCallback(() => {
    const video = videoRef.current;
    if (video.paused) {
      video.play();
    } else if (video) {
      video.pause();
    }
  }, []);

  const handleMute = useCallback(() => {
    const video = videoRef.current;
    video.muted = !mute;
    setMute(!mute);
  }, [mute]);

  const handleControlsOverlay = useCallback(() => {
    setShowControls(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, controlsTs);
  }, [controlsTs]);

  const handleDurationUpdate = useCallback(() => {
    setPlayTime(videoRef.current.currentTime);
    setPercentage(
      (videoRef.current.currentTime / videoRef.current.duration) * 100
    );
  }, []);

  const handleVideoEnded = useCallback(() => {
    videoRef.current.pause();
    setPercentage(0);
    setPlayTime(0);
  }, []);

  const handleVideoPlaying = useCallback(() => {
    handleControlsOverlay();
  }, [handleControlsOverlay]);

  const toggleFullScreen = () => {
    const video = videoRef.current;
    if (video.webkitEnterFullscreen) {
      video.webkitEnterFullscreen();
    } else {
      video.requestFullscreen({ navigationUI: 'show' });
    }
  };

  const handleFullScreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      toggleFullScreen();
    }
  }, []);

  // video element event listeners
  useEffect(() => {
    const video = videoRef.current;
    video.addEventListener('timeupdate', handleDurationUpdate, false);
    video.addEventListener('ended', handleVideoEnded, false);
    video.addEventListener('playing', handleVideoPlaying, false);
    return () => {
      video.removeEventListener('timeupdate', handleDurationUpdate, false);
      video.removeEventListener('ended', handleVideoEnded, false);
      video.removeEventListener('playing', handleVideoPlaying, false);
    };
  }, [handleDurationUpdate, handleVideoEnded, handleVideoPlaying]);

  // clean up
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    video.currentTime = (video.duration / 100) * Number(e.target.value);
    setPercentage(Number(e.target.value));
  };

  // double tap to fullscreen on mobile
  const handleDoubleTap = useDoubleTap(handleFullScreen);

  const mobileProps = isMobile
    ? {
        onTouchStart: handleDoubleTap,
        onDoubleClick: handleDoubleTap,
      }
    : {};

  return (
    <Container as="figure" className={style.container}>
      <video
        ref={videoRef}
        onClick={() => {
          handleControlsOverlay();
          handlePlay();
        }}
        autoPlay={autoPlay}
        muted={mute}
        poster={thumbnail}
        playsInline={true}
        {...mobileProps}
        {...rest}
      >
        <source src={src} type={formats.join(',')} />
        <div style={{ width: '100%', background: 'white' }}></div>
      </video>

      {showControls && (
        <div>
          <div className={style.progressInfo}>
            {isMobile && (
              <Button className={style.fullScreen} onClick={handleFullScreen}>
                <FullscreenToggle toggle={false} />
              </Button>
            )}
            <Body color={'white'} align="right">
              {formatDuration(playTime)} / {getDuration()}
            </Body>
            <div className={style.progressContainer}>
              {showTimer && (
                <div
                  className={style.timerInfo}
                  style={{
                    left: `calc(${percentage}% - 25px)`,
                  }}
                >
                  <Small xs={false}>{formatDuration(playTime)}</Small>
                </div>
              )}

              <ProgressBar
                max={100}
                currentTime={percentage}
                onSeek={handleSeek}
                onMouseEnter={() => setShowTimer(true)}
                onMouseLeave={() => setShowTimer(false)}
              />
            </div>
          </div>
          <div className={style.controlButtons}>
            <Button onClick={handlePlay} className={style.playPause}>
              {videoRef.current && !videoRef.current?.paused ? (
                <Pause />
              ) : (
                <PlayButton />
              )}
            </Button>
            <Button onClick={handleMute} className={style.muteSound}>
              {videoRef.current && videoRef.current.muted ? (
                <Mute />
              ) : (
                <Sound />
              )}
            </Button>
          </div>
        </div>
      )}
    </Container>
  );
};

export default VideoPlayer;
